import React, { useEffect } from "react";
import styles from "./BottomFindBar.module.css";

/**
 * Presentational navigation for search results.
 * Handles ← / → and Enter for result navigation.
 */
export default function FindResultsNav({
  hasResults,
  isSearching,
  querySequence,
  activeMatchIndex,
  matchStartIndices,
  onPrev,
  onNext,
  hasPendingSearch = false,
}) {
  // Utility: ignore key events when typing in inputs/textareas/contenteditable
  const isEditableTarget = (el) => {
    if (!el) return false;
    const tag = el.tagName?.toLowerCase();
    const editableTags = ["input", "textarea", "select"];
    if (editableTags.includes(tag)) return true;
    if (el.isContentEditable) return true;
    return false;
  };

  // Keyboard navigation while the panel is open
  useEffect(() => {
    const onKey = (e) => {
      if (!hasResults) return;

      // Left / Right to move across matches (existing behavior)
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        onPrev?.();
        return;
      }
      if (e.key === "ArrowRight") {
        e.preventDefault();
        onNext?.();
        return;
      }

      // NEW: Enter jumps to NEXT match (wraps), but only when not editing a new query
      if (e.key === "Enter") {
        // If user is typing in an input/textarea or has changed query (pending search),
        // don't hijack Enter — let the form submit instead.
        if (isEditableTarget(document.activeElement)) return;
        if (hasPendingSearch) return;

        e.preventDefault();
        onNext?.();
        return;
      }
    };

    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [hasResults, onPrev, onNext, hasPendingSearch]);

  return (
    <div className={styles.resultsRow} aria-live="polite">
      {hasResults ? (
        <>
          <button
            className={styles.navBtn}
            aria-label="Previous match"
            onClick={onPrev}
            type="button"
          >
            ←
          </button>
          <span className={styles.resultInfo}>
            <b>Match {activeMatchIndex + 1}</b> of{" "}
            <b>{matchStartIndices.length}</b>
            {"  "}
            <span className={styles.resultPos}>
              (at <b>{matchStartIndices[activeMatchIndex] + 1}</b>
              {querySequence &&
                `–${
                  matchStartIndices[activeMatchIndex] + querySequence.length
                }`}
              )
            </span>
          </span>
          <button
            className={styles.navBtn}
            aria-label="Next match"
            onClick={onNext}
            type="button"
          >
            →
          </button>
        </>
      ) : querySequence ? (
        <span className={styles.noResults}>
          {isSearching ? "Searching…" : "No matches found."}
        </span>
      ) : (
        <span className={styles.hint}>Enter a sequence to search</span>
      )}
    </div>
  );
}
